import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewEncapsulation, inject, signal } from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatOptionModule } from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatSelectModule } from '@angular/material/select'
import { MatTooltipModule } from '@angular/material/tooltip'
import { Company, CreatePendencyInput, Maintenance, Pendency, UpdatePendencyInput } from '@generated/sammathnaur.graphql'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { isEqual } from 'lodash-es'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { Subject, takeUntil } from 'rxjs'
import { noSpacesValidator } from './validators/no-spaces.validator'
import { CompaniesService } from '../../core/api/companies/companies.service'
import { PendenciesService } from '../../core/api/pendencies/pendencies.service'
import { PendencyTypeService } from '../../core/api/pendency-types/pendency-types.service'
import { UsersService } from '../../core/api/users/users.service'
import { FcMaintenanceSelectComponent } from '../fc-maintenance-select/fc-maintenance-select.component'
import { FcSelectMultiplePagedComponent } from '../fc-select-multiple-paged/fc-select-multiple-paged.component'
import { FcSelectPagedComponent } from '../fc-select-paged/fc-select-paged.component'

@Component({
    selector: 'app-create-or-edit-pendency-dialog',
    templateUrl: './create-or-edit-pendency-dialog.component.html',
    styleUrls: ['./create-or-edit-pendency-dialog.component.scss', '../../../styles.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
    imports: [
        CommonModule,
        FormsModule,
        FcSelectMultiplePagedComponent,
        FcSelectPagedComponent,
        FcMaintenanceSelectComponent,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatTooltipModule,
        MatProgressBarModule,
        NgScrollbarModule,
        ReactiveFormsModule,
        TranslateModule
    ]
})
export class CreateOrEditPendencyDialogComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public pendency!: Pendency
  @Input() public maintenance!: Maintenance
  @Input() public hasCanEditPermission: boolean = true
  @Output() public readonly closeDialogEvent = new EventEmitter<any>()

  public nameMaxLength = 100
  public descriptionMaxLength = 2000

  public enableSaveButton = signal<boolean>(false)
  public showInfo = signal<boolean>(true)
  public showPendencyTypeRequiredError = signal<boolean>(false)
  public showCompanyRequiredError = signal<boolean>(false)
  public isOnEditDialog = signal<boolean>(false)
  public isLoading = signal<boolean>(false)
  public isOnViewMode = signal<boolean>(false)
  public companiesIds = signal<string[] | null>(null)
  public ignoredAssigneeIds = signal<string[]>([])
  public ignoredReaderIds = signal<string[]>([])
  private isInitializing = false


  public pendenciesService = inject(PendenciesService)
  public pendencyTypeService = inject(PendencyTypeService)
  public usersService = inject(UsersService)
  public companiesService = inject(CompaniesService)
  public translateService = inject(TranslateService)
  public cdr = inject(ChangeDetectorRef)

  private destroy$ = new Subject<void>()
  public treatmentTypeService = {
    offset: signal(0),
    limit: signal(2),
    totalCount: signal(2),
    pageInfo: signal({ hasNextPage: false }),
    isLoading: signal(false),
    searchPage: async () => {
      const options = [
        { id: 'internal', name: this.translateService.instant('CREATE_OR_EDIT_PENDENCY_DIALOG.PENDENCY.TREATMENT_TYPE.INTERNAL') },
        { id: 'external', name: this.translateService.instant('CREATE_OR_EDIT_PENDENCY_DIALOG.PENDENCY.TREATMENT_TYPE.EXTERNAL') }
      ]
      return { data: { treatmentTypes: { items: options, totalCount: 2, pageInfo: { hasNextPage: false } } } }
    }
  }

  public pendencyForm = new FormGroup({
    id: new FormControl(''),
    title: new FormControl('', {
      validators: [
        Validators.required,
        Validators.maxLength(this.nameMaxLength),
        noSpacesValidator
      ]
    }),
    description: new FormControl('', {
      validators: [
        Validators.maxLength(this.descriptionMaxLength),
        noSpacesValidator
      ]
    }),
    pendencyType: new FormControl(null, [Validators.required]),
    treatmentType: new FormControl(null, [Validators.required]),
    assignees: new FormControl<any[]>([], [Validators.required]),
    readers: new FormControl<any[]>([]),
    company: new FormControl(null),
    maintenance: new FormControl({ value: null, disabled: true })
  })

  public ngOnInit(): void {
    this.disableMaintenanceFieldIfCompanyIsNotSelected()
    this.watchTreatmentTypeChanges()
    this.handleTreatmentTypeChange(this.pendencyForm.get('treatmentType')?.value)
    this.manageSaveButtonState()
  }

  public async ngOnChanges(changes: any): Promise<void> {
    if (!changes.pendency?.currentValue && changes.pendency?.firstChange) {
      return
    }

    this.setInitialValues()
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public async save(): Promise<void> {
    this.isLoading.set(true)

    try {
      const pendency = this.getPendencyToSave()
      const { data } = await this.pendenciesService.save(pendency)
      const message = this.isOnEditDialog() ? 'SUCCESSFULLY_UPDATED' : 'SUCCESSFULLY_SAVED'
      this.closeDialogEvent.emit({ pendency: data, message })
      this.isLoading.set(false)
    } catch {
      const message = this.isOnEditDialog() ? 'ERROR_UPDATING' : 'ERROR_SAVING'
      this.closeDialogEvent.emit({ error: true, message })
      this.isLoading.set(false)
    }
  }

  public closeDialog(): void {
    this.closeDialogEvent.emit({ reload: false })
  }

  private watchTreatmentTypeChanges(): void {
    this.pendencyForm.get('treatmentType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((treatmentType: any) => {
      this.handleTreatmentTypeChange(treatmentType)
    })
  }

  private handleTreatmentTypeChange(treatmentType: any): void {
    const companyControl = this.pendencyForm.get('company')
    const maintenanceControl = this.pendencyForm.get('maintenance')
    if (treatmentType?.id === 'external') {
      companyControl?.enable()
      companyControl?.setValidators([Validators.required])
    } else {
      companyControl?.disable()
      companyControl?.clearValidators()
      companyControl?.setValue(null)

      if (treatmentType?.id === 'internal') {
        this.companiesIds.set(null)

        if (!this.isOnViewMode() && this.hasCanEditPermission) {
          maintenanceControl?.enable()
        }
      }
    }

    companyControl?.updateValueAndValidity()
  }

  private manageSaveButtonState(): void {
    this.pendencyForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateSaveButtonState()
    })

    this.watchAssigneesAndReadersChanges()
  }

  private updateSaveButtonState(): void {
    if (!this.isOnEditDialog()) {
      this.enableSaveButton.set(this.pendencyForm.valid && (this.pendencyForm.dirty || this.pendencyForm.touched))
      this.cdr.detectChanges()
      return
    }

    this.enableSaveButton.set(
      this.pendencyForm.valid &&
      this.pendencyIsChanged()
    )
    this.cdr.detectChanges()
  }

  private watchAssigneesAndReadersChanges(): void {
    this.pendencyForm.get('assignees')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((assignees: any[] | null) => {
      const ids = (assignees || []).map(a => a.id)
      this.ignoredReaderIds.set(ids)
    })

    this.pendencyForm.get('readers')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((readers: any[] | null) => {
      const ids = (readers || []).map(reader => reader.id)
      this.ignoredAssigneeIds.set(ids)
    })
  }

  private setInitialValues(): void {
    this.isInitializing = true
    this.isOnEditDialog.set(Boolean(this.pendency?.id))
    const { id, title, description, pendencyType, company, maintenance, readers, treatmentType }: any = this.pendency || {}
    const assignees = this.getAssignees(this.pendency)
    this.pendencyForm.patchValue({
      id,
      title,
      description,
      pendencyType,
      company: company || (this.maintenance ? this.maintenance.company : null),
      maintenance: maintenance || this.maintenance || null,
      treatmentType: this.getInitialTreatmentType(treatmentType),
      assignees: (assignees as any[]) || [],
      readers: (readers as any[]) || []
    })
    this.handleTreatmentTypeChange(this.pendencyForm.get('treatmentType')?.value)
    this.disableMaintenanceFieldIfCompanyIsNotSelected()
    this.updateSaveButtonState()
    this.isInitializing = false
  }

  public getAssignees(pendency: any): any[] {
    if (pendency?.assignees && pendency?.assignees?.length > 0) {
      return pendency.assignees
    }

    return pendency?.assignee ? [pendency.assignee] : []
  }

  private getInitialTreatmentType(id = 'internal'): any {
    const treatmentTypeId = id
    return {
      id: treatmentTypeId,
      name: this.translateService.instant(`CREATE_OR_EDIT_PENDENCY_DIALOG.PENDENCY.TREATMENT_TYPE.${treatmentTypeId.toUpperCase()}`)
    }
  }

  private pendencyIsChanged(): boolean {
    const { title, description, pendencyType, company, maintenance, assignees, readers, treatmentType }: any = this.pendencyForm.getRawValue()

    return !isEqual(
      {
        title: title?.trim(),
        description: description?.trim(),
        pendencyType: (pendencyType as any)?.id,
        company: (company as any)?.id,
        maintenance: (maintenance as any)?.id,
        assigneesIds: ((assignees as any[]) || []).map((assignee: any) => assignee.id).sort((a, b) => a.localeCompare(b)),
        readersIds: ((readers as any[]) || []).map((reader: any) => reader.id).sort((a, b) => a.localeCompare(b)),
        treatmentType: (treatmentType as any)?.id
      },
      {
        title: this.pendency.title?.trim(),
        description: this.pendency.description?.trim(),
        pendencyType: this.pendency.pendencyType?.id,
        company: this.pendency.company?.id,
        maintenance: this.pendency.maintenance?.id,
        assigneesIds: (this.getAssignees(this.pendency)).map((assignee: any) => assignee.id).sort((a, b) => a.localeCompare(b)),
        readersIds: (this.pendency.readers || []).map((reader: any) => reader.id).sort((a, b) => a.localeCompare(b)),
        treatmentType: this.pendency.treatmentType
      }
    )
  }

  private disableMaintenanceFieldIfCompanyIsNotSelected(): void {
    this.pendencyForm.get('company')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((company: Company | null) => {
      if (this.isInitializing) return

      this.pendencyForm.get('maintenance')?.setValue(null, { emitEvent: false })

      const isInternal = (this.pendencyForm.get('treatmentType')?.value as any)?.id === 'internal'
      if (!company && this.pendencyForm.get('maintenance')?.enabled && !isInternal) {
        this.pendencyForm.get('maintenance')?.disable()
        return
      }

      if (!company || this.maintenance) {
        return
      }

      this.companiesIds.set([company.id])

      if (this.isOnViewMode() || !this.hasCanEditPermission) {
        return
      }

      this.pendencyForm.get('maintenance')?.enable()
    })

    if (this.maintenance?.company) {
      this.pendencyForm.get('company')?.disable()
    }

    if ((this.pendency?.status === 'done' || !this.hasCanEditPermission) && this.pendency?.id) {
      this.isOnViewMode.set(true)
      this.pendencyForm.disable()
    }

    this.pendencyForm.get('company')?.updateValueAndValidity({ emitEvent: false })

    const company = this.pendencyForm.get('company')?.value as Company | null
    if (company && !this.maintenance) {
      this.companiesIds.set([company.id])

      if (!this.isOnViewMode() && this.hasCanEditPermission) {
        this.pendencyForm.get('maintenance')?.enable()
      }
    } else if (!company) {
      const isInternal = (this.pendencyForm.get('treatmentType')?.value as any)?.id === 'internal'
      if (!isInternal) {
        this.pendencyForm.get('maintenance')?.disable()
      } else if (!this.isOnViewMode() && this.hasCanEditPermission) {
        this.companiesIds.set(null)
        this.pendencyForm.get('maintenance')?.enable()
      }
    }

    this.pendencyForm.get('maintenance')?.updateValueAndValidity({ emitEvent: false })
  }

  private getPendencyToSave(): CreatePendencyInput | UpdatePendencyInput {
    const pendency: any = this.pendencyForm.getRawValue()
    const pendencyToSave: any = {
      ...(pendency.id && { id: pendency.id }),
      title: pendency.title,
      description: pendency.description,
      pendencyTypeId: pendency.pendencyType?.id,
      companyId: pendency.company?.id || null,
      maintenanceId: pendency.maintenance?.id || null,
      assigneeIds: pendency.assignees?.map((assignee: any) => assignee.id) || [],
      readerIds: pendency.readers?.map((reader: any) => reader.id) || [],
      treatmentType: pendency.treatmentType?.id
    }

    return pendencyToSave
  }
}
